import { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";
import {
  Building2, Users, Award, Calendar as Cal, TrendingUp, IndianRupee, Download,
  FileSpreadsheet, FileText, Printer, Search, Bookmark, Save, Loader2, Filter, ArrowRight, UserPlus,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const KINDS = [
  { key: "schools", label: "Schools", icon: Building2 },
  { key: "teachers", label: "Teachers", icon: Users },
  { key: "programs", label: "Programs", icon: TrendingUp },
  { key: "bookings", label: "Bookings", icon: Cal },
  { key: "certificates", label: "Certificates", icon: Award },
  { key: "registrations", label: "Registrations", icon: UserPlus },
];

const TEMPLATES_KEY = "edulift.reports.templates.v1";
const readTemplates = () => { try { return JSON.parse(localStorage.getItem(TEMPLATES_KEY) || "[]"); } catch { return []; } };
const writeTemplates = (list) => localStorage.setItem(TEMPLATES_KEY, JSON.stringify(list));

const STATUSES_BY_KIND = {
  bookings: ["pending", "approved", "rejected", "completed"],
  programs: [],
  certificates: [],
  schools: [],
  teachers: [],
};

const fmt = (v) => (v === null || v === undefined ? "" : typeof v === "boolean" ? (v ? "Yes" : "No") : typeof v === "number" ? v.toLocaleString() : String(v));

export default function Reports() {
  const { user } = useAuth();
  const [kind, setKind] = useState("bookings");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState("");
  const [templates, setTemplates] = useState(readTemplates());
  const [templateName, setTemplateName] = useState("");

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (status) p.set("status", status);
    return p;
  }, [q, dateFrom, dateTo, status]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dataRes, sumRes] = await Promise.all([
        api.get(`/reports/data/${kind}?${params.toString()}`),
        api.get(`/reports/summary?${params.toString()}`),
      ]);
      setRows(dataRes.data.rows || []);
      setColumns(dataRes.data.columns || []);
      setSummary(sumRes.data || null);
    } catch (e) {
      toast.error(`Could not load ${kind} report`);
    } finally {
      setLoading(false);
    }
  }, [kind, params]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (fmt2) => {
    setExporting(fmt2);
    try {
      const token = localStorage.getItem("token") || "";
      const url = `${BACKEND}/api/reports/export/${kind}?format=${fmt2}&${params.toString()}`;
      const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      link.download = `edulift-${kind}-${stamp}.${fmt2}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${fmt2.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Export ${fmt2.toUpperCase()} failed`);
    } finally { setExporting(""); }
  };

  const saveTemplate = () => {
    const name = (templateName || "").trim();
    if (!name) { toast.error("Give the template a name"); return; }
    const t = { id: Date.now(), name, kind, q, dateFrom, dateTo, status };
    const next = [t, ...templates.filter((x) => x.name !== name)];
    setTemplates(next); writeTemplates(next); setTemplateName("");
    toast.success("Template saved");
  };
  const applyTemplate = (t) => { setKind(t.kind); setQ(t.q); setDateFrom(t.dateFrom); setDateTo(t.dateTo); setStatus(t.status); };
  const deleteTemplate = (id) => { const next = templates.filter((t) => t.id !== id); setTemplates(next); writeTemplates(next); };

  // Chart series from rows: group counts by month
  const chartData = useMemo(() => {
    const groups = {};
    rows.forEach((r) => {
      const src = r.created_at || r.preferred_date || r.date;
      if (!src) return;
      const d = new Date(src);
      if (isNaN(d)) return;
      const key = d.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
      groups[key] = (groups[key] || 0) + 1;
    });
    return Object.entries(groups).map(([k, v]) => ({ label: k, count: v })).reverse();
  }, [rows]);

  const KIND_ICON = KINDS.find((k) => k.key === kind)?.icon || TrendingUp;

  const summaryCards = [
    { key: "schools", label: "Schools", icon: Building2, tone: "bg-indigo-50 text-indigo-600" },
    { key: "teachers", label: "Teachers", icon: Users, tone: "bg-emerald-50 text-emerald-600" },
    { key: "programs", label: "Programs", icon: TrendingUp, tone: "bg-sky-50 text-sky-600" },
    { key: "bookings", label: "Bookings", icon: Cal, tone: "bg-rose-50 text-rose-600" },
    { key: "certificates", label: "Certificates", icon: Award, tone: "bg-amber-50 text-amber-600" },
    { key: "attendance", label: "Attendance", icon: Users, tone: "bg-violet-50 text-violet-600" },
    { key: "revenue", label: "Revenue (₹)", icon: IndianRupee, tone: "bg-teal-50 text-teal-600" },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap print:hidden">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Operational insights across the training operation.</p>
        </div>
        <button onClick={() => window.print()} data-testid="reports-print" className="inline-flex items-center gap-1.5 border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-lg text-gray-700">
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
      </div>

      {/* Summary strip */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {summaryCards.map((c) => {
          const v = summary?.[c.key];
          const show = v !== undefined && v !== null;
          return (
            <div key={c.key} className="rounded-xl border border-gray-200 bg-white p-4" data-testid={`summary-${c.key}`}>
              <div className={`w-8 h-8 grid place-items-center rounded-lg ${c.tone}`}><c.icon className="w-4 h-4" /></div>
              <div className="mt-3 font-display text-xl font-semibold tabular-nums">
                {loading ? <span className="inline-block h-5 w-14 bg-gray-100 animate-pulse rounded" /> : show ? (c.key === "revenue" ? `₹${Number(v).toLocaleString()}` : Number(v).toLocaleString()) : "—"}
              </div>
              <div className="text-[11px] text-gray-500 font-medium mt-0.5">{c.label}</div>
            </div>
          );
        })}
      </div>

      {/* Filters + Kind tabs */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 print:hidden">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="reports-kind-tabs">
          {KINDS.map((k) => {
            const active = k.key === kind;
            return (
              <button
                key={k.key} onClick={() => setKind(k.key)}
                data-testid={`kind-${k.key}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                  active ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                }`}
              >
                <k.icon className="w-3.5 h-3.5" /> {k.label}
              </button>
            );
          })}
        </div>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <div className="relative sm:col-span-2">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="reports-search" className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm" />
          </div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="reports-date-from" className="px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm" />
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="reports-date-to" className="px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm" />
          {STATUSES_BY_KIND[kind]?.length ? (
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="reports-status" className="px-3 py-2 rounded-lg border border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10 outline-none text-sm bg-white">
              <option value="">All statuses</option>
              {STATUSES_BY_KIND[kind].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : <div />}
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-gray-500 inline-flex items-center gap-1.5"><Filter className="w-3 h-3" /> {rows.length} row{rows.length === 1 ? "" : "s"}</div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => exportFile("csv")} disabled={!!exporting} data-testid="export-csv"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50">
              {exporting === "csv" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} CSV
            </button>
            <button onClick={() => exportFile("xlsx")} disabled={!!exporting} data-testid="export-xlsx"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
              {exporting === "xlsx" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />} Excel
            </button>
            <button onClick={() => exportFile("pdf")} disabled={!!exporting} data-testid="export-pdf"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50">
              {exporting === "pdf" ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} PDF
            </button>
          </div>
        </div>
      </div>

      {/* Chart + Templates */}
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5"><KIND_ICON className="w-4 h-4 text-[#4F46E5]" /> {kind[0].toUpperCase() + kind.slice(1)} · trend</div>
          </div>
          <div className="h-[240px]" data-testid="reports-chart">
            {chartData.length === 0 ? (
              <div className="h-full grid place-items-center text-xs text-gray-500">No data in current filter</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {kind === "certificates" || kind === "bookings" ? (
                  <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip cursor={{ fill: "rgba(79,70,229,0.05)" }} />
                    <Bar dataKey="count" fill="#4F46E5" radius={[6, 6, 0, 0]} maxBarSize={28} />
                  </BarChart>
                ) : (
                  <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#4F46E5" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                )}
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="text-sm font-semibold text-gray-900 mb-3 inline-flex items-center gap-1.5"><Bookmark className="w-4 h-4 text-[#4F46E5]" /> Saved templates</div>
          <div className="flex gap-2 mb-3">
            <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Name this view" data-testid="template-name" className="flex-1 px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10" />
            <button onClick={saveTemplate} data-testid="template-save" className="inline-flex items-center gap-1 bg-[#0F172A] text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-[#1e293b]"><Save className="w-3 h-3" /> Save</button>
          </div>
          {templates.length === 0 ? (
            <div className="text-xs text-gray-500 py-4 text-center">No templates yet</div>
          ) : (
            <ul className="space-y-1.5 max-h-[190px] overflow-y-auto">
              {templates.map((t) => (
                <li key={t.id} data-testid={`template-item-${t.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-gray-50 group">
                  <button onClick={() => applyTemplate(t)} className="flex-1 text-left text-xs">
                    <div className="font-medium text-gray-900 truncate">{t.name}</div>
                    <div className="text-[10px] text-gray-500">{t.kind} · {t.dateFrom || "*"} → {t.dateTo || "*"}</div>
                  </button>
                  <button onClick={() => deleteTemplate(t.id)} className="text-[10px] text-gray-400 hover:text-rose-600 opacity-0 group-hover:opacity-100">Delete</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Data table */}
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-900">
          {kind[0].toUpperCase() + kind.slice(1)} · {rows.length} rows
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-10 grid place-items-center text-sm text-gray-500 inline-flex gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-gray-500" data-testid="reports-empty">No records match your filter.</div>
          ) : (
            <table className="w-full text-sm" data-testid="reports-table">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500 tracking-wider">
                <tr>
                  {columns.map((c) => (
                    <th key={c} className="text-left px-4 py-2.5 font-semibold whitespace-nowrap">{c.replace(/_/g, " ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((r, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50/60">
                    {columns.map((c) => (
                      <td key={c} className="px-4 py-2.5 text-gray-700 whitespace-nowrap max-w-[200px] truncate">{fmt(r[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {rows.length > 200 && (
          <div className="px-5 py-2.5 border-t border-gray-100 text-[11px] text-gray-500">Showing first 200 · export for the full list</div>
        )}
      </div>
    </motion.div>
  );
}
